/**
 * Config file for address templates used in different countries.
 *
 * Note: There is no single source of thruth, and the below mentioned sources
 * are sometimes contradicting. This file is our own workout.
 *
 * Note 2: The e-commerce address does not have to match the envelope address
 * format published by national postal office or UPU.
 *
 * Note 3: google maps service is more ofter wrong than right on address
 * format.
 *
 * Sources:
 * - https://www.upu.int/en/Postal-Solutions/Programmes-Services/Addressing-Solutions
 * - https://raw.githubusercontent.com/OpenCageData/address-formatting/master/conf/countries/worldwide.yaml
 * - https://en.wikipedia.org/wiki/Address
 */

export type AddressPartName =
  | 'street'
  | 'street_number'
  | 'apartment_number'
  | 'address_line_1'
  | 'address_line_2'
  | 'postal_code'
  | 'city'
  | 'region_code';

export type TemplatePart = AddressPartName | 'comma' | 'newline';

export type AddressTemplate = Array<TemplatePart>;

/* Street level general templates */

/**
 * In general there should be either street + street number or 1st address
 * line.
 */
const secondaryAddressLine: AddressTemplate = [
  'address_line_1',
  'comma',
  'address_line_2',
  'newline',
];

const defaultAddressLine: AddressTemplate = [
  'street',
  'street_number',
  'comma',
  ...secondaryAddressLine,
];

const reversedAddressLine: AddressTemplate = [
  'street_number',
  'street',
  'comma',
  ...secondaryAddressLine,
];

/* Country templates */

// Default template - most often used
const europeanTemplate: AddressTemplate = [...defaultAddressLine, 'postal_code', 'city'];

const russianTemplate: AddressTemplate = [...defaultAddressLine, 'city', 'postal_code'];

const frenchTemplate: AddressTemplate = [...reversedAddressLine, 'postal_code', 'city'];

const britishTemplate: AddressTemplate = [...reversedAddressLine, 'city', 'postal_code'];

const americanTemplate: AddressTemplate = [
  ...reversedAddressLine,
  'city',
  'comma',
  'region_code',
  'postal_code',
];

const arabTemplate: AddressTemplate = [...reversedAddressLine, 'city'];

const addressTemplateConfig: { [key: string]: AddressTemplate } = {
  AD: frenchTemplate,
  AE: arabTemplate,
  AL: europeanTemplate,
  AM: frenchTemplate,
  AO: russianTemplate,
  AR: europeanTemplate,
  AS: americanTemplate,
  AT: europeanTemplate,
  AU: [
    ...reversedAddressLine,
    'city', // no comma after city.
    'region_code',
    'postal_code',
  ],
  AX: europeanTemplate,
  AZ: frenchTemplate,
  BA: europeanTemplate,
  BB: arabTemplate,
  BE: europeanTemplate,
  BG: europeanTemplate,
  BH: britishTemplate,
  BM: russianTemplate,
  BN: britishTemplate,
  BR: russianTemplate,
  BY: [...defaultAddressLine, 'postal_code', 'comma', 'city'],
  CA: americanTemplate,
  CH: europeanTemplate,
  CI: arabTemplate,
  CL: europeanTemplate,
  CN: frenchTemplate,
  /*
   * CO: It is uncertain whether postal code comes before or after city name in
   * Colombia.
   */
  CO: russianTemplate,
  CR: russianTemplate,
  CY: europeanTemplate,
  CZ: europeanTemplate,
  DE: europeanTemplate,
  DK: europeanTemplate,
  DZ: frenchTemplate,
  EE: europeanTemplate,
  EG: britishTemplate,
  ES: [
    'street',
    'comma', // additional comma after street.
    'street_number',
    'comma',
    'address_line_1',
    'comma',
    'address_line_2',
    'newline',
    'postal_code',
    'city',
  ],
  FI: europeanTemplate,
  FO: europeanTemplate,
  FR: frenchTemplate,
  GB: britishTemplate,
  GE: europeanTemplate,
  GG: britishTemplate,
  GH: arabTemplate,
  GI: britishTemplate,
  GL: europeanTemplate,
  GP: frenchTemplate,
  GR: europeanTemplate,
  GT: europeanTemplate,
  GU: americanTemplate,
  HK: arabTemplate,
  HR: europeanTemplate,
  /**
   * Hungary is kind of unusual because city name comes first.
   */
  HU: ['city', 'comma', ...defaultAddressLine, 'postal_code'],
  ID: russianTemplate,
  IE: britishTemplate,
  IL: europeanTemplate,
  IM: britishTemplate,
  IN: britishTemplate,
  IO: britishTemplate,
  IQ: britishTemplate,
  IS: europeanTemplate,
  IT: europeanTemplate,
  JE: britishTemplate,
  JO: europeanTemplate,
  JP: britishTemplate,
  KH: britishTemplate,
  KR: britishTemplate,
  KW: europeanTemplate,
  KZ: [...defaultAddressLine, 'postal_code', 'comma', 'city'],
  LB: britishTemplate,
  LI: europeanTemplate,
  LT: europeanTemplate,
  LU: frenchTemplate,
  LV: [...defaultAddressLine, 'city', 'comma', 'postal_code'],
  MA: frenchTemplate,
  MC: frenchTemplate,
  MD: [...defaultAddressLine, 'postal_code', 'comma', 'city'],
  ME: europeanTemplate,
  MK: europeanTemplate,
  MN: europeanTemplate,
  MO: [...defaultAddressLine, 'city'],
  MQ: frenchTemplate,
  MT: britishTemplate,
  MU: arabTemplate,
  MX: europeanTemplate,
  MV: russianTemplate,
  MY: frenchTemplate,
  NC: frenchTemplate,
  NG: britishTemplate,
  NL: europeanTemplate,
  NO: europeanTemplate,
  NZ: britishTemplate,
  OM: frenchTemplate,
  PE: russianTemplate,
  PF: frenchTemplate,
  PH: russianTemplate,
  PK: russianTemplate,
  PL: europeanTemplate,
  PR: americanTemplate,
  PT: europeanTemplate,
  QA: [...defaultAddressLine, 'city'],
  RE: frenchTemplate,
  /**
   * RO: sources for Romania are contradicting, here we just use European
   * format.
   */
  RO: europeanTemplate,
  RS: europeanTemplate,
  RU: russianTemplate,
  SA: britishTemplate,
  SE: europeanTemplate,
  SG: britishTemplate,
  SI: europeanTemplate,
  SK: europeanTemplate,
  SM: europeanTemplate,
  TH: britishTemplate,
  TR: europeanTemplate,
  TW: britishTemplate,
  UA: russianTemplate,
  UM: americanTemplate,
  US: americanTemplate,
  UY: europeanTemplate,
  UZ: russianTemplate,
  VA: europeanTemplate,
  VE: russianTemplate,
  VI: americanTemplate,
  VN: britishTemplate,
  ZA: britishTemplate,
};

export function getAddressTemplateConfig(country: string) {
  return addressTemplateConfig[country] || europeanTemplate;
}

/**
 * These templates are used only to present the address in the ui
 *
 * Countries without changes were commented out
 * for sake of readability/consistency between both objects - feel free to edit those if requirements change
 */

const secondaryPresentationalAddressLine: AddressTemplate = [
  'address_line_1',
  'comma',
  'address_line_2',
];

const defaultPresentationalAddressLine: AddressTemplate = [
  'street',
  'street_number',
  'comma',
  'apartment_number',
  'comma',
  ...secondaryAddressLine,
];

const europeanPresentationalTemplate: AddressTemplate = [
  ...defaultPresentationalAddressLine,
  'postal_code',
  'city',
];

const reversedPresentationalAddressLine: AddressTemplate = [
  'street_number',
  'street',
  'comma',
  ...secondaryPresentationalAddressLine,
];

const russianPresentationalTemplate: AddressTemplate = [
  ...defaultPresentationalAddressLine,
  'city',
  'postal_code',
];

const americanPresentationalTemplate: AddressTemplate = [
  ...reversedPresentationalAddressLine,
  'city',
  'newline',
  'region_code',
  'postal_code',
];

const presentationalAddressTemplateConfig: { [key: string]: AddressTemplate } = {
  // AD: frenchTemplate,
  // AE: arabTemplate,
  AL: europeanPresentationalTemplate,
  // AM: frenchTemplate,
  AO: russianPresentationalTemplate,
  AR: europeanPresentationalTemplate,
  AS: americanPresentationalTemplate,
  AT: europeanPresentationalTemplate,
  // AU: [
  //   ...reversedAddressLine,
  //   'city', // no comma after city.
  //   'region_code',
  //   'postal_code',
  // ],
  AX: europeanPresentationalTemplate,
  // AZ: frenchTemplate,
  BA: europeanPresentationalTemplate,
  // BB: arabTemplate,
  BE: europeanPresentationalTemplate,
  BG: europeanPresentationalTemplate,
  // BH: britishTemplate,
  BM: russianPresentationalTemplate,
  // BN: britishTemplate,
  BR: russianPresentationalTemplate,
  BY: [...defaultPresentationalAddressLine, 'postal_code', 'comma', 'city'],
  CA: americanPresentationalTemplate,
  CH: europeanPresentationalTemplate,
  // CI: arabTemplate,
  CL: europeanPresentationalTemplate,
  // CN: frenchTemplate,
  CO: russianPresentationalTemplate,
  CR: russianPresentationalTemplate,
  CY: europeanPresentationalTemplate,
  CZ: europeanPresentationalTemplate,
  DE: europeanPresentationalTemplate,
  DK: europeanPresentationalTemplate,
  // DZ: frenchTemplate,
  EE: europeanPresentationalTemplate,
  // EG: britishTemplate,
  // ES: [
  //   'street',
  //   'comma', // additional comma after street.
  //   'street_number',
  //   'comma',
  //   'address_line_1',
  //   'comma',
  //   'address_line_2',
  //   'newline',
  //   'postal_code',
  //   'city',
  // ],
  FI: europeanPresentationalTemplate,
  FO: europeanPresentationalTemplate,
  // FR: frenchTemplate,
  // GB: britishTemplate,
  GE: europeanPresentationalTemplate,
  // GG: britishTemplate,
  // GH: arabTemplate,
  // GI: britishTemplate,
  GL: europeanPresentationalTemplate,
  // GP: frenchTemplate,
  GR: europeanPresentationalTemplate,
  GT: europeanPresentationalTemplate,
  GU: americanPresentationalTemplate,
  // HK: arabTemplate,
  HR: europeanPresentationalTemplate,
  /**
   * Hungary is kind of unusual because city name comes first.
   */
  HU: ['city', 'comma', ...defaultPresentationalAddressLine, 'postal_code'],
  ID: russianPresentationalTemplate,
  // IE: britishTemplate,
  IL: europeanPresentationalTemplate,
  // IM: britishTemplate,
  // IN: britishTemplate,
  // IO: britishTemplate,
  // IQ: britishTemplate,
  IS: europeanPresentationalTemplate,
  IT: europeanPresentationalTemplate,
  // JE: britishTemplate,
  JO: europeanPresentationalTemplate,
  // JP: britishTemplate,
  // KH: britishTemplate,
  // KR: britishTemplate,
  KW: europeanPresentationalTemplate,
  KZ: [...defaultPresentationalAddressLine, 'postal_code', 'comma', 'city'],
  // LB: britishTemplate,
  LI: europeanPresentationalTemplate,
  LT: europeanPresentationalTemplate,
  // LU: frenchTemplate,
  LV: [...defaultPresentationalAddressLine, 'city', 'comma', 'postal_code'],
  // MA: frenchTemplate,
  // MC: frenchTemplate,
  MD: [...defaultPresentationalAddressLine, 'postal_code', 'comma', 'city'],
  ME: europeanPresentationalTemplate,
  MK: europeanPresentationalTemplate,
  MN: europeanPresentationalTemplate,
  MO: [...defaultPresentationalAddressLine, 'city'],
  // MQ: frenchTemplate,
  // MT: britishTemplate,
  // MU: arabTemplate,
  MX: europeanPresentationalTemplate,
  MV: russianPresentationalTemplate,
  // MY: frenchTemplate,
  // NC: frenchTemplate,
  // NG: britishTemplate,
  NL: europeanPresentationalTemplate,
  NO: europeanPresentationalTemplate,
  // NZ: britishTemplate,
  // OM: frenchTemplate,
  PE: russianPresentationalTemplate,
  // PF: frenchTemplate,
  PH: russianPresentationalTemplate,
  PK: russianPresentationalTemplate,
  PL: europeanPresentationalTemplate,
  PR: americanPresentationalTemplate,
  PT: europeanPresentationalTemplate,
  QA: [...defaultPresentationalAddressLine, 'city'],
  // RE: frenchTemplate,
  RO: europeanPresentationalTemplate,
  RS: europeanPresentationalTemplate,
  RU: russianPresentationalTemplate,
  // SA: britishTemplate,
  SE: europeanPresentationalTemplate,
  // SG: britishTemplate,
  SI: europeanPresentationalTemplate,
  SK: europeanPresentationalTemplate,
  SM: europeanPresentationalTemplate,
  // TH: britishTemplate,
  TR: europeanPresentationalTemplate,
  // TW: britishTemplate,
  UA: russianPresentationalTemplate,
  UM: americanPresentationalTemplate,
  US: americanPresentationalTemplate,
  UY: europeanPresentationalTemplate,
  UZ: russianPresentationalTemplate,
  VA: europeanPresentationalTemplate,
  VE: russianPresentationalTemplate,
  VI: americanPresentationalTemplate,
  // VN: britishTemplate,
  // ZA: britishTemplate,
};

export function getPresentationalTemplateConfig(country: string) {
  return (
    presentationalAddressTemplateConfig[country] ||
    addressTemplateConfig[country] ||
    europeanPresentationalTemplate
  );
}

const streetNumberReversalConfig: { [key: string]: boolean } = {
  AD: true,
  AE: true,
  AM: true,
  AU: true,
  AZ: true,
  BB: true,
  BH: true,
  BN: true,
  CA: true,
  CI: true,
  CN: true,
  DZ: true,
  EG: true,
  FR: true,
  GB: true,
  GG: true,
  GI: true,
  GH: true,
  GP: true,
  GU: true,
  HK: true,
  IE: true,
  IM: true,
  IN: true,
  IO: true,
  IQ: true,
  KH: true,
  KR: true,
  JE: true,
  JP: true,
  LB: true,
  LU: true,
  MA: true,
  MC: true,
  MQ: true,
  MT: true,
  MU: true,
  MY: true,
  NC: true,
  NG: true,
  NZ: true,
  OM: true,
  PF: true,
  PR: true,
  RE: true,
  SA: true,
  SG: true,
  TH: true,
  TW: true,
  UM: true,
  US: true,
  VI: true,
  VN: true,
  ZA: true,
};

export function isStreetNumberReversed(country: string): boolean {
  return streetNumberReversalConfig[country] || false;
}
